<template>
  <div class="admin-mail-templates-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('adminMailTemplates.pageTitle') }}</h1>
        <p class="page-subtitle">
          {{ t('adminMailTemplates.pageSubtitle') }}
        </p>
      </div>
      <v-btn
        color="#EA5545"
        variant="flat"
        prepend-icon="mdi-plus"
        @click="openCreateDialog"
      >
        {{ t('adminMailTemplates.actions.new') }}
      </v-btn>
    </div>

    <v-alert
      v-if="errorMessage"
      type="error"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <v-alert
      v-if="successMessage"
      type="success"
      class="mb-4"
      closable
      @click:close="successMessage = ''"
    >
      {{ successMessage }}
    </v-alert>

    <div v-if="isLoading" class="loading-wrapper">
      <v-progress-circular indeterminate color="#EA5545" size="64" />
      <p class="state-text">{{ t('adminMailTemplates.loading') }}</p>
    </div>

    <div v-else>
      <p v-if="!mailTemplates.length" class="state-text">
        {{ t('adminMailTemplates.empty') }}
      </p>

      <v-row v-else dense>
        <v-col
          v-for="template in mailTemplates"
          :key="template.mail_id"
          cols="12"
          md="6"
        >
          <v-card
            class="template-card summary-card"
            elevation="1"
            role="button"
            tabindex="0"
            @click="openTemplate(template)"
            @keydown.enter="openTemplate(template)"
          >
            <div class="summary-header">
              <v-chip size="small" color="#EA5545" class="mr-2" variant="flat">
                #{{ template.mail_id }}
              </v-chip>
              <div class="summary-texts">
                <span class="summary-title">
                  {{ template.mail_subject || t('adminMailTemplates.templateTitle', { id: template.mail_id }) }}
                </span>
                <span class="summary-body">
                  {{ formatPreview(template.mail_text) }}
                </span>
              </div>
            </div>
            <v-icon size="18" color="#EA5545">mdi-pencil</v-icon>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-dialog
      v-model="showEditor"
      max-width="720"
    >
      <v-card>
        <v-card-title class="dialog-title">
          <div class="d-flex align-center">
            <v-icon class="mr-2" color="#EA5545">mdi-email-edit</v-icon>
            <span>
              {{ t('adminMailTemplates.templateTitle', { id: currentTemplate?.mail_id || '–' }) }}
            </span>
          </div>
          <v-spacer />
          <v-btn icon variant="text" @click="closeEditor">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-text-field
            v-model="editBuffer.mail_subject"
            :label="t('adminMailTemplates.fields.subject')"
            variant="outlined"
            density="comfortable"
            class="mb-4"
          />
          <v-textarea
            v-model="editBuffer.mail_text"
            :label="t('adminMailTemplates.fields.body')"
            variant="outlined"
            rows="10"
            auto-grow
            class="mb-4"
          />
          <v-text-field
            v-model="editBuffer.mail_attach_1"
            :label="t('adminMailTemplates.fields.attach')"
            variant="outlined"
            density="comfortable"
            hint=".pdf oder Pfad – optional"
            persistent-hint
          />
          <v-text-field
            v-model="editBuffer.mail_test_link"
            :label="t('adminMailTemplates.fields.testLink')"
            variant="outlined"
            density="comfortable"
            hint="https://… (optional)"
            persistent-hint
          />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeEditor">
            {{ t('adminMailTemplates.actions.cancel') }}
          </v-btn>
          <v-btn
            color="#EA5545"
            variant="flat"
            :loading="isTemplateSaving(currentTemplate?.mail_id)"
            @click="saveTemplate"
          >
            <v-icon left size="16">mdi-content-save</v-icon>
            {{ t('adminMailTemplates.actions.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue'
import { useLanguage } from '@/composables/useLanguage'
import { useSuccessStore } from '@/stores/successStore'
import { useErrorStore } from '@/stores/errorStore'

const baseCalls = inject('baseCalls')
const updateDocumentHead = inject('updateDocumentHead')
const successStore = useSuccessStore()
const errorStore = useErrorStore()
const { t } = useLanguage()

const mailTemplates = ref([])
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const savingState = ref({})
const showEditor = ref(false)
const currentTemplate = ref(null)
const isCreatingNew = ref(false)
const editBuffer = ref({
  mail_id: null,
  mail_subject: '',
  mail_text: '',
  mail_attach_1: '',
  mail_test_link: ''
})

async function loadMailTemplates() {
  isLoading.value = true
  errorMessage.value = ''
  try {
    const response = await baseCalls.userPost({
      action: 'getmailtemplates'
    })
    if (response.data?.success) {
      mailTemplates.value = (response.data.mailtexts || []).map((entry) => ({
        ...entry,
        mail_subject: entry.mail_subject ?? '',
        mail_text: entry.mail_text ?? '',
        mail_attach_1: entry.mail_attach_1 ?? '',
        mail_test_link: entry.mail_test_link ?? ''
      }))
    } else {
      errorMessage.value = response.data?.message || t('adminMailTemplates.errors.loadFailed')
      mailTemplates.value = []
    }
  } catch (error) {
    console.error('loadMailTemplates', error)
    errorMessage.value = t('adminMailTemplates.errors.loadFailed')
    errorStore.setError(true, errorMessage.value)
  } finally {
    isLoading.value = false
  }
}

function setTemplateSaving(id, value) {
  const key = id ?? 'new'
  savingState.value = {
    ...savingState.value,
    [key]: value
  }
}

function isTemplateSaving(id) {
  const key = id ?? 'new'
  return Boolean(savingState.value[key])
}

function openCreateDialog() {
  isCreatingNew.value = true
  currentTemplate.value = null
  editBuffer.value = {
    mail_id: null,
    mail_subject: '',
    mail_text: '',
    mail_attach_1: '',
    mail_test_link: ''
  }
  successMessage.value = ''
  errorMessage.value = ''
  showEditor.value = true
}

function openTemplate(template) {
  currentTemplate.value = { ...template }
  isCreatingNew.value = false
  editBuffer.value = {
    mail_id: template.mail_id,
    mail_subject: template.mail_subject ?? '',
    mail_text: template.mail_text ?? '',
    mail_attach_1: template.mail_attach_1 ?? '',
    mail_test_link: template.mail_test_link ?? ''
  }
  successMessage.value = ''
  errorMessage.value = ''
  showEditor.value = true
}

function closeEditor() {
  showEditor.value = false
  setTemplateSaving(currentTemplate.value?.mail_id, false)
  currentTemplate.value = null
  isCreatingNew.value = false
}

function formatPreview(text = '') {
  if (!text) return t('adminMailTemplates.previewPlaceholder')
  const trimmed = text.replace(/\s+/g, ' ').trim()
  if (trimmed.length <= 120) {
    return trimmed
  }
  return trimmed.substring(0, 120) + '…'
}

async function saveTemplate() {
  if (!isCreatingNew.value && !currentTemplate.value) return

  const payload = {
    action: isCreatingNew.value ? 'createmailtemplate' : 'updatemailtemplate',
    mail_subject: editBuffer.value.mail_subject ?? '',
    mail_text: editBuffer.value.mail_text ?? '',
    mail_attach_1: editBuffer.value.mail_attach_1 ?? '',
    mail_test_link: editBuffer.value.mail_test_link ?? ''
  }

  if (!isCreatingNew.value) {
    payload.mail_id = editBuffer.value.mail_id
  }

  if ((payload.mail_subject || '').trim() === '') {
    errorMessage.value = t('adminMailTemplates.errors.subjectRequired')
    errorStore.setError(true, errorMessage.value)
    return
  }

  setTemplateSaving(isCreatingNew.value ? null : currentTemplate.value.mail_id, true)
  successMessage.value = ''
  errorMessage.value = ''

  try {
    const response = await baseCalls.userPost(payload)
    if (response.data?.success) {
      successMessage.value = t('adminMailTemplates.success')
      successStore.setSuccess(true, '', successMessage.value)
      if (isCreatingNew.value) {
        const created = response.data.mailtext || {
          mail_id: response.data.mail_id ?? Date.now(),
          mail_subject: payload.mail_subject,
          mail_text: payload.mail_text,
          mail_attach_1: payload.mail_attach_1,
          mail_test_link: payload.mail_test_link
        }
        mailTemplates.value = [
          {
            ...created,
            mail_subject: created.mail_subject ?? '',
            mail_text: created.mail_text ?? '',
            mail_attach_1: created.mail_attach_1 ?? '',
            mail_test_link: created.mail_test_link ?? ''
          },
          ...mailTemplates.value
        ]
      } else {
        const index = mailTemplates.value.findIndex(tpl => tpl.mail_id === editBuffer.value.mail_id)
        if (index !== -1) {
          mailTemplates.value[index] = {
            ...mailTemplates.value[index],
            mail_subject: editBuffer.value.mail_subject,
            mail_text: editBuffer.value.mail_text,
            mail_attach_1: editBuffer.value.mail_attach_1,
            mail_test_link: editBuffer.value.mail_test_link
          }
        }
      }
      closeEditor()
    } else {
      const message = response.data?.message || t('adminMailTemplates.errors.saveFailed')
      errorMessage.value = message
      errorStore.setError(true, message)
    }
  } catch (error) {
    console.error('saveTemplate', error)
    const message = t('adminMailTemplates.errors.saveFailed')
    errorMessage.value = message
    errorStore.setError(true, message)
  } finally {
    setTemplateSaving(isCreatingNew.value ? null : currentTemplate.value?.mail_id, false)
  }
}

onMounted(() => {
  updateDocumentHead?.(
    t('adminMailTemplates.pageTitle'),
    t('adminMailTemplates.pageSubtitle')
  )
  loadMailTemplates()
})
</script>

<style scoped src="@/assets/styles/sfc/components/sites/AdminMailTemplates.css"></style>
